/**
 * Commerce blocks — extracted from ActionBlockRenderer.jsx (v119 refactor).
 *
 * Includes: ProductBlock, LeadFormBlock, CouponBlock, PaidCouponCheckout,
 * FeaturedProductBlock, BuyNowGridButton, ProductBuyModal, PaidCouponBlock.
 * All exports are re-exported from ActionBlockRenderer.jsx so existing
 * imports across the app continue to work with zero churn.
 */
import React, { useState, useEffect, useRef } from "react";
import ReactDOM, { createPortal } from "react-dom";
import { Copy, CheckCircle2, Tag, ShoppingBag, Timer, Send, Lock, Loader2, Users, Gift, X, Calendar } from "lucide-react";
import PhoneInput from "../PhoneInput";
import MarketingConsentCheckbox from "../MarketingConsentCheckbox";
import EventAttendanceChip from "../EventAttendanceChip";
import { StorefrontBuyModal } from "../../pages/Storefront";
import { WatchReelButton } from "../WatchReelButton";
import { _postPublicLead } from "./_shared";
import PurchaseAuthGate from "../PurchaseAuthGate";
import { useAuth } from "../../context/AuthContext";
import { externalLinkClickHandler, openExternalUrl } from "../../lib/openExternal";
import { cleanText, cleanPrefill } from "../../lib/razorpayOptions";
import useCreatorLoyalty from "../../hooks/useCreatorLoyalty";
import CoinEarnChip from "../CoinEarnChip";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { sizedImage } from "../../lib/imageUrl";
import { ProductInitials } from "../commerce/ProductThumb";

// ---------------------------------------------------------------------------
// v147.73 — ProductImageCarousel
// Horizontally scrollable snap-carousel used by every commerce block (bio
// hub featured product, storefront cards, cart line items). Renders the
// full image via `object-contain` on a neutral backdrop so posters (tall)
// and product shots (wide) both fit without cropping — this replaces the
// old `object-cover` behaviour that clipped the top + bottom of tall
// event posters like the user's Rox Event ticket.
// Behaviour:
//   * 1 image → static, no controls.
//   * 2+ images → snap-mandatory scroll, dot indicators, and arrow
//     buttons on ≥ md so keyboard/mouse users can page through.
//   * Empty → the item's initials when `name` is given (v149.70), else
//     the cream placeholder with the ShoppingBag glyph. A picture that
//     fails to load is dropped the same way — never a broken-image icon.
// ---------------------------------------------------------------------------
export function ProductImageCarousel({
  images, align = "center", testIdPrefix = "product-image", heightClass, name,
}) {
  const [broken, setBroken] = useState(() => new Set());
  const list = (images || []).filter((src) => src && !broken.has(src));
  const [idx, setIdx] = useState(0);
  const scrollerRef = React.useRef(null);
  // Height envelope. Keeps things bounded on tiny phones and readable on
  // wider screens while never cropping tall assets.
  const box = heightClass
    || (align === "center"
      ? "h-64 sm:h-80"
      : "h-56 sm:h-full min-h-[14rem]");
  const border = align === "center"
    ? "border-b-4 border-ink"
    : "sm:border-r-4 sm:border-b-0 border-b-4 border-ink";

  if (list.length === 0) {
    return (
      <div className={`w-full ${box} ${border} bg-cream flex items-center justify-center`}>
        {name
          ? <ProductInitials name={name} className="w-full h-full" testid={`${testIdPrefix}-initials`} />
          : <ShoppingBag className="w-14 h-14 text-ink/30" />}
      </div>
    );
  }

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    const cur = Math.round(el.scrollLeft / w);
    if (cur !== idx) setIdx(cur);
  };
  const goto = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(list.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
    setIdx(clamped);
  };

  return (
    <div className={`relative w-full ${box} ${border} bg-cream group`}>
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-none"
        data-testid={`${testIdPrefix}-carousel`}
      >
        {list.map((src, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-full h-full snap-start flex items-center justify-center p-1"
          >
            <img
              src={sizedImage(src, 960)}
              alt=""
              loading="lazy"
              decoding="async"
              onError={() => setBroken((b) => new Set(b).add(src))}
              className="max-w-full max-h-full w-auto h-auto object-contain"
              data-testid={i === 0 ? testIdPrefix : `${testIdPrefix}-${i}`}
            />
          </div>
        ))}
      </div>
      {list.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => goto(idx - 1)}
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-2 w-8 h-8 items-center justify-center border-2 border-ink bg-white shadow-brutalSm disabled:opacity-30"
            disabled={idx === 0}
            aria-label="Previous image"
            data-testid={`${testIdPrefix}-prev`}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={3} />
          </button>
          <button
            type="button"
            onClick={() => goto(idx + 1)}
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-2 w-8 h-8 items-center justify-center border-2 border-ink bg-white shadow-brutalSm disabled:opacity-30"
            disabled={idx >= list.length - 1}
            aria-label="Next image"
            data-testid={`${testIdPrefix}-next`}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={3} />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goto(i)}
                aria-label={`Go to image ${i + 1}`}
                className={`w-1.5 h-1.5 border border-ink ${i === idx ? "bg-ink" : "bg-white"}`}
                data-testid={`${testIdPrefix}-dot-${i}`}
              />
            ))}
          </div>
          <span
            className="absolute top-2 right-2 border-2 border-ink bg-white text-[10px] font-black px-1.5 py-0.5 shadow-brutalSm"
            data-testid={`${testIdPrefix}-counter`}
          >
            {idx + 1} / {list.length}
          </span>
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// v147.75 — ProductInfoSheet
// Bottom-sheet modal used by every commerce block (featured product, grid
// card, storefront). Opens on image tap and shows the FULL description +
// swipeable image gallery. Renders via portal so it escapes overflow
// clipping from parent block cards.
// ---------------------------------------------------------------------------
export function ProductInfoSheet({ open, onClose, product, onCTA, ctaLabel, ctaTestId, ctaDisabled }) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open || !product) return null;
  const imgs = (product.image_urls && product.image_urls.length)
    ? product.image_urls
    : (product.image_url ? [product.image_url] : []);
  const currencySym = product.currency === "USD" ? "$" : (product.currency === "EUR" ? "€" : "₹");
  // v147.99.16 — Auto-derive a Book Now / Buy Now label + icon when the
  // caller doesn't pass one. Services + appointments book, everything
  // else buys.
  const kind = (product.kind || product.type || "product").toLowerCase();
  const isService = kind === "service" || kind === "appointment" || kind === "booking";
  const CTAIcon = isService ? Calendar : ShoppingBag;
  const derivedLabel = ctaLabel || (isService ? "Book Now" : "Buy Now");
  const showCTA = typeof onCTA === "function";
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center animate-in fade-in"
      onClick={onClose}
      data-testid={`product-info-sheet-${product.product_id || "unknown"}`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg max-h-[90dvh] bg-white border-t-4 sm:border-4 border-ink shadow-brutal flex flex-col overflow-hidden animate-in slide-in-from-bottom"
      >
        {/* Sheet handle + close */}
        <div className="flex-shrink-0 flex items-center justify-between p-2 border-b-2 border-ink bg-cream">
          <div className="mx-auto sm:mx-0 w-10 h-1.5 bg-ink/30 rounded-full sm:hidden" />
          <button
            type="button"
            onClick={onClose}
            className="border-2 border-ink bg-white p-1.5 shadow-brutalSm"
            aria-label="Close"
            data-testid="product-info-sheet-close"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {imgs.length > 0 && (
            <div className="border-b-2 border-ink">
              <ProductImageCarousel
                images={imgs}
                name={product.title}
                align="center"
                heightClass="h-72 sm:h-96"
                testIdPrefix={`product-info-sheet-image-${product.product_id || "x"}`}
              />
            </div>
          )}
          <div className="p-4 sm:p-5 space-y-3">
            <div className="space-y-1">
              <p className="font-display font-black text-xl sm:text-2xl leading-tight" data-testid="product-info-sheet-title">
                {product.title}
              </p>
              {typeof product.price === "number" && product.price > 0 && (
                <p className="font-black text-lg text-coral">
                  {currencySym}{Number(product.price).toLocaleString("en-IN")}
                </p>
              )}
            </div>
            {product.description && (
              <p
                className="text-sm leading-relaxed whitespace-pre-wrap text-ink/80"
                data-testid="product-info-sheet-description"
              >
                {product.description}
              </p>
            )}
          </div>
        </div>

        {/* v147.99.16 — Sticky footer CTA. Rendered when an `onCTA`
            handler is supplied OR when the product is in a disabled
            terminal state (sold out / sales closed) and a label was
            provided — so buyers see the state inside the sheet
            instead of an empty footer. */}
        {(showCTA || (ctaDisabled && ctaLabel)) && (
          <div className="flex-shrink-0 border-t-2 border-ink bg-white p-3">
            <button
              type="button"
              disabled={!!ctaDisabled || !showCTA}
              onClick={() => { if (showCTA && !ctaDisabled) onCTA(); }}
              data-testid={ctaTestId || "product-info-sheet-cta"}
              className={`w-full border-2 border-ink px-4 py-2.5 font-black text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 ${(ctaDisabled || !showCTA) ? "bg-white text-ink/40 cursor-not-allowed" : "bg-coral text-white hover:translate-y-[1px] transition"}`}
            >
              <CTAIcon className="w-4 h-4" strokeWidth={3} /> {derivedLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// v147.75 — Truncate a string to `n` chars keeping word boundaries.
export function truncateWords(text, n = 140) {
  if (!text) return "";
  const s = String(text).trim();
  if (s.length <= n) return s;
  const cut = s.slice(0, n);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
}



export function ProductBlock({ block, onClick, editable, hubContext }) {
  const c = block.config || {};
  const { user } = useAuth();
  // v55.3 — c.currency stores an ISO code (INR/USD/EUR/GBP) not a symbol.
  // Map to the correct display symbol; default to ₹ (platform default).
  const CURR_SYM = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
  const symbol = CURR_SYM[String(c.currency || "INR").toUpperCase()] || "₹";
  // v145.60 — Only display price when it's actually set (> 0). Products
  // published without a price should stay clean (no "₹0.00") and hide
  // the Buy CTA — visitors are meant to "Contact seller" instead.
  const hasPrice = c.price != null && Number(c.price) > 0;
  const price = hasPrice ? `${symbol}${Number(c.price).toFixed(2)}` : null;
  // v145.61 — Creator pass-on discount on picked (affiliate) products.
  const listPrice = Number(c.list_price_inr || 0);
  const discountPct = Number(c.customer_discount_pct || 0);
  const hasCreatorDiscount = c.is_affiliate && discountPct > 0 && listPrice > Number(c.price || 0);
  // v145.62 — Social proof for affiliate/picked single-product blocks.
  const socialProofCount = Number(c.social_proof_count || 0);
  const showSocialProof = !!c.is_affiliate && socialProofCount > 0;
  // v97 — Razorpay Buy Now. When the creator enables Razorpay checkout
  // (`enable_razorpay:true`) OR there's no `buy_url`, we render a modal
  // buy flow instead of an outbound link.
  const isINR = String(c.currency || "INR").toUpperCase() === "INR";
  const useRazorpay = !editable && !!c.product_id && isINR &&
                      (c.enable_razorpay || !c.buy_url);
  // v145.60 — Suppress the Buy / Buy-now button entirely when price is 0.
  // External buy_url still lets the seller take orders off-platform.
  const showCta = hasPrice || !!c.buy_url;
  const [showAuth, setShowAuth] = useState(false);
  const [showBuy, setShowBuy] = useState(false);
  const creatorSlug = hubContext?.creator?.slug || hubContext?.slug || null;
  // v146.42 — Coin earn preview on the product CTA. Reads the creator's
  // public loyalty program config; renders nothing if disabled.
  const loyalty = useCreatorLoyalty(creatorSlug);
  const coinsPreview = loyalty.coinsForAmount(Number(c.price || 0));

  const handleBuyClick = () => {
    if (user?.user_id) setShowBuy(true);
    else setShowAuth(true);
  };
  return (
    <div className="ah-block flex gap-3 items-center" data-testid={`block-product-${block.block_id}`}>
      {c.image_url && (
        <img src={sizedImage(c.image_url, 160)} alt="" className="w-16 h-16 object-cover flex-shrink-0" style={{ borderRadius: "var(--ah-block-radius)" }} loading="lazy" decoding="async" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">{c.title || "Product"}</p>
        {c.description && <p className="text-xs ah-muted line-clamp-2">{c.description}</p>}
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {price && (
            hasCreatorDiscount ? (
              <>
                <span className="font-bold text-sm ah-accent">{price}</span>
                <span className="text-[10px] text-ink/50 line-through tabular-nums">
                  Shop {symbol}{listPrice.toLocaleString("en-IN")}
                </span>
                <span className="text-[9px] font-black uppercase border border-ink bg-sun px-1">
                  −{discountPct}%
                </span>
              </>
            ) : (
              <span className="font-bold text-sm ah-accent">{price}</span>
            )
          )}
          {c.social_link && (
            <WatchReelButton url={c.social_link} testid={`block-product-reel-${block.block_id}`} />
          )}
          {showSocialProof && (
            <span
              className="inline-flex items-center gap-1 border-2 border-ink bg-mint px-1 py-0.5 text-[9px] font-black uppercase tracking-wider"
              data-testid={`block-product-social-proof-${block.block_id}`}
            >
              <Users className="w-2.5 h-2.5" strokeWidth={2.5} />
              {socialProofCount} bought
            </span>
          )}
          {coinsPreview > 0 && (
            <CoinEarnChip
              coins={coinsPreview}
              currentBalance={loyalty.balance}
              coinName={loyalty.coin_name}
              testId={`block-product-earn-${block.block_id}`}
            />
          )}
        </div>
      </div>
      {useRazorpay && showCta ? (
        <button
          type="button"
          onClick={handleBuyClick}
          className="border-2 px-3 py-1 font-bold text-xs flex items-center gap-1 flex-shrink-0"
          style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
          data-testid={`block-product-buy-now-${block.block_id}`}
        >
          <ShoppingBag className="w-3 h-3" /> Buy now
        </button>
      ) : showCta ? (
        <a
          href={c.buy_url || "#"}
          target={editable ? undefined : "_blank"}
          rel="noreferrer"
          onClick={(e) => onClick(e, c.buy_url)}
          className="border-2 px-3 py-1 font-bold text-xs flex items-center gap-1 flex-shrink-0"
          style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
          data-testid={`block-product-buy-${block.block_id}`}
        >
          <ShoppingBag className="w-3 h-3" /> Buy
        </a>
      ) : null}
      {showAuth && (
        <PurchaseAuthGate
          creatorSlug={creatorSlug}
          intent="buy"
          priceInr={Number(c.price) || 0}
          itemTitle={c.title}
          onClose={() => setShowAuth(false)}
          onAuthed={() => { setShowAuth(false); setShowBuy(true); }}
        />
      )}
      {showBuy && (
        // v145.20 — Use StorefrontBuyModal so single-product blocks
        // also expose Loyalty Coin redemption + coupons + service
        // date/slot pickers. Config from the block already carries
        // product_id, price, kind, currency, image_url, slots, etc.
        // v145.22 — pass `creator_user_id` from hubContext so
        // LoyaltyCoinPanel can look up the creator's coin program and
        // show the buyer's balance/apply slider at checkout. Without
        // this the panel silently no-ops (bio-hub public endpoint
        // doesn't inline creator_user_id on each product config).
        <StorefrontBuyModal
          product={(() => {
            // v147.65 — Look up the fresh product from the bio-hub
            // context (which now carries ticket_tiers/event/venue/
            // availability from the backend projection). Spreading it
            // fixes the "Please pick a ticket tier" 400 that happened
            // when block.config held a stale product snapshot without
            // ticket fields. Fall back to block.config so blocks with
            // no matching context (e.g. deleted product) still work.
            const fresh = (hubContext?.products || []).find(
              (p) => p.product_id === c.product_id,
            ) || {};
            return {
              ...c,
              ...fresh,
              product_id: c.product_id,
              title: c.title || fresh.title,
              price: Number(c.price) || Number(fresh.price) || 0,
              currency: c.currency || fresh.currency || "INR",
              kind: c.kind || fresh.kind || "product",
              image_url: c.image_url || fresh.image_url,
              slots: c.slots || fresh.slots,
              available_days: c.available_days || fresh.available_days,
              instructions: c.instructions || fresh.instructions,
              enable_razorpay: c.enable_razorpay,
              description: c.description || fresh.description,
              social_link: c.social_link || fresh.social_link,
              creator_user_id: c.creator_user_id
                || fresh.creator_user_id
                || hubContext?.creator?.user_id,
            };
          })()}
          slug={creatorSlug}
          buyerUser={user}
          // v146.68 — When this is a brand-collab product, the OTP
          // requirement comes from the BRAND's storefront settings
          // (embedded on the asset by `_hydrate_asset`). Fall back to
          // the current hub's settings otherwise.
          requireOtp={
            c.require_phone_otp !== undefined
              ? !!c.require_phone_otp
              : !!(hubContext?.settings?.require_phone_otp)
          }
          otpChannel={c.otp_channel || hubContext?.settings?.otp_channel || "auto"}
          codEnabled={!!hubContext?.settings?.cod_enabled}
          onlineEnabled={hubContext?.settings?.online_enabled !== false}
          onClose={() => setShowBuy(false)}
          onSuccess={() => setShowBuy(false)}
        />
      )}
    </div>
  );
}

export function LeadFormBlock({ block, editable }) {
  const c = block.config || {};
  // v145.25 — Auto-fill name / email / phone when the visitor is
  // signed in so we don't ask them for details we already have. They
  // can still edit — the auth-derived values just prefill the state.
  const { user } = useAuth();
  const [state, setState] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    message: "",
    // v146.44 — WhatsApp broadcast opt-in captured with the phone number.
    marketing_consent: true,
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | ok | err
  // Keep the form in sync when auth resolves after mount (e.g. slow
  // session load). Only overwrites blank fields so a mid-edit typed
  // value isn't clobbered.
  useEffect(() => {
    if (!user) return;
    setState((prev) => ({
      name: prev.name || user.name || "",
      email: prev.email || user.email || "",
      phone: prev.phone || user.phone || "",
      message: prev.message,
    }));
  }, [user?.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (editable) return;
    setStatus("submitting");
    try {
      await _postPublicLead(block, { ...state });
      setStatus("ok");
      setState({ name: "", email: "", phone: "", message: "", marketing_consent: true });
    } catch {
      setStatus("err");
    }
  };

  const fields = c.fields || ["name", "email"];
  const showMessage = fields.includes("message");
  return (
    <form onSubmit={submit} className="ah-block space-y-2" data-testid={`block-lead-form-${block.block_id}`}>
      <p className="font-bold text-sm text-center">{c.headline || "Get in touch"}</p>
      {c.subtext && <p className="text-xs ah-muted text-center">{c.subtext}</p>}
      {fields.includes("name") && (
        <input
          type="text" placeholder="Your name"
          value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })}
          className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
          data-testid={`lead-input-name-${block.block_id}`}
        />
      )}
      {fields.includes("email") && (
        <input
          type="email" placeholder="you@example.com" required
          value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })}
          className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
          data-testid={`lead-input-email-${block.block_id}`}
        />
      )}
      {fields.includes("phone") && (
        <>
          <PhoneInput
            value={state.phone}
            onChange={(v) => setState({ ...state, phone: v })}
            placeholder="10-digit mobile number"
            data-testid={`lead-input-phone-${block.block_id}`}
            className="w-full"
          />
          <MarketingConsentCheckbox
            checked={state.marketing_consent}
            onChange={(v) => setState({ ...state, marketing_consent: v })}
            testid={`lead-consent-${block.block_id}`}
            compact
          />
        </>
      )}
      {showMessage && (
        <textarea
          placeholder="Message" rows={2}
          value={state.message} onChange={(e) => setState({ ...state, message: e.target.value })}
          className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
          data-testid={`lead-input-message-${block.block_id}`}
        />
      )}
      <button
        type="submit"
        disabled={status === "submitting" || editable}
        className="w-full border-2 font-bold text-sm py-1.5 flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
        data-testid={`lead-submit-${block.block_id}`}
      >
        <Send className="w-3 h-3" /> {c.button_label || "Submit"}
      </button>
      {status === "ok" && <p className="text-xs font-bold text-center ah-accent" data-testid={`lead-thanks-${block.block_id}`}>Thanks! We&apos;ll be in touch.</p>}
      {status === "err" && <p className="text-xs font-bold text-center" style={{ color: "#dc2626" }}>Something went wrong.</p>}
    </form>
  );
}

export function CouponBlock({ block, editable, hubContext }) {
  const c = block.config || {};
  const { user } = useAuth();
  const backend = process.env.REACT_APP_BACKEND_URL;
  const couponId = c.coupon_id;
  const unlockPrice = Number(c.unlock_price || 0);
  const isPaid = unlockPrice > 0 && couponId;
  const creatorSlug = hubContext?.creator?.slug || hubContext?.slug || null;

  // Store purchase_id per coupon_id in localStorage so refresh keeps the
  // unlocked state without needing user auth. Buyers identify by email.
  const storageKey = couponId ? `paid_coupon_${couponId}` : null;
  const [unlocked, setUnlocked] = useState(() => {
    if (!isPaid || editable) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [showBuy, setShowBuy] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [copied, setCopied] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  // Rehydrate the unlocked view from server on mount (in case the coupon
  // was edited or the localStorage record is stale).
  useEffect(() => {
    if (!isPaid || editable || !unlocked?.purchase_id) return;
    fetch(`${backend}/api/public/coupons/${encodeURIComponent(couponId)}/preview?purchase_id=${encodeURIComponent(unlocked.purchase_id)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && !d.locked) setUnlocked(d); })
      .catch(() => {});
  }, [backend, couponId, editable, isPaid, unlocked?.purchase_id]);

  // v142.2 — Loyalty coin-unlock eligibility. Only meaningful for a
  // signed-in user viewing a locked paid coupon whose creator runs a
  // loyalty program with a positive `coupon_unlock_cost`.
  const [coinUnlock, setCoinUnlock] = useState(null);  // {eligible, balance, cost, coin_name}
  const [coinUnlocking, setCoinUnlocking] = useState(false);
  const [coinUnlockErr, setCoinUnlockErr] = useState("");
  useEffect(() => {
    if (!isPaid || editable || !couponId || unlocked?.code) return;
    let cancelled = false;
    (async () => {
      try {
        // Fetch the coupon preview once to learn the creator's user_id.
        const pr = await fetch(
          `${backend}/api/public/coupons/${encodeURIComponent(couponId)}/preview`,
          { credentials: "include" },
        );
        if (!pr.ok) return;
        const pd = await pr.json();
        const bizId = pd?.creator_user_id;
        if (!bizId) return;
        const lr = await fetch(
          `${backend}/api/loyalty/coupon-unlock-preview?business_user_id=${encodeURIComponent(bizId)}`,
          { credentials: "include" },
        );
        if (lr.status === 401) return;  // anonymous — hide button
        if (!lr.ok) return;
        const ld = await lr.json();
        if (!cancelled) setCoinUnlock({ ...ld, business_user_id: bizId });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [backend, couponId, editable, isPaid, unlocked?.code]);

  const doCoinUnlock = async () => {
    if (!coinUnlock?.eligible || coinUnlocking) return;
    setCoinUnlocking(true); setCoinUnlockErr("");
    try {
      const r = await fetch(`${backend}/api/loyalty/unlock-coupon`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          business_user_id: coinUnlock.business_user_id,
          coupon_id: couponId,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Could not unlock");
      // v145.22 — Coin balance just dropped by the unlock cost; nudge
      // the header pill to re-fetch so the drop is visible instantly.
      try { window.dispatchEvent(new CustomEvent("coins:changed")); } catch { /* noop */ }
      // Server returns full unlocked view (matches PaidCouponCheckout onSuccess shape).
      if (d.coupon) {
        setUnlocked(d.coupon);
        try { localStorage.setItem(storageKey, JSON.stringify(d.coupon)); } catch { /* noop */ }
      }
    } catch (e) {
      setCoinUnlockErr(e.message || "Could not unlock");
    } finally {
      setCoinUnlocking(false);
    }
  };

  const code = unlocked?.code || (!isPaid ? c.code : null);
  const showLocked = isPaid && !code;

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (!editable && couponId && !redeemed) {
        setRedeemed(true);
        fetch(`${backend}/api/public/coupons/${encodeURIComponent(couponId)}/redeem`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }).catch(() => {});
      }
    } catch { /* clipboard blocked */ }
  };

  if (showLocked) {
    return (
      <div className="ah-block w-full" data-testid={`block-coupon-locked-${block.block_id}`}>
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 flex-shrink-0 ah-accent" strokeWidth={2.5} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm truncate">
              {c.teaser_headline || c.headline || `${c.discount_pct || ""}% off — unlock to reveal code`}
            </p>
            {c.teaser_description && (
              <p className="text-xs ah-muted mt-1 line-clamp-2">{c.teaser_description}</p>
            )}
            {c.terms && (
              <p className="text-[10px] ah-muted mt-1 italic line-clamp-1">Terms: {c.terms}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => (user?.user_id ? setShowBuy(true) : setShowAuth(true))}
            disabled={editable}
            data-testid={`block-coupon-unlock-btn-${block.block_id}`}
            className="text-xs font-bold border-2 border-ink bg-coral text-white px-3 py-1.5 shadow-brutalSm flex-shrink-0"
          >
            Unlock ₹{unlockPrice}
          </button>
        </div>
        {/* v142.2 — Coin-based alternative unlock, only for signed-in
            users of a business that has a positive coupon_unlock_cost. */}
        {coinUnlock && coinUnlock.cost > 0 && (
          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`block-coupon-coin-unlock-${block.block_id}`}>
            <button
              type="button"
              onClick={doCoinUnlock}
              disabled={!coinUnlock.eligible || coinUnlocking || editable}
              data-testid={`block-coupon-coin-unlock-btn-${block.block_id}`}
              className="text-[11px] font-bold border-2 border-ink bg-sun px-3 py-1.5 shadow-brutalSm inline-flex items-center gap-1 disabled:opacity-60"
            >
              {coinUnlocking ? <Loader2 className="w-3 h-3 animate-spin" /> : <span aria-hidden>✦</span>}
              Or unlock with {coinUnlock.cost} {coinUnlock.coin_name}
            </button>
            <span className="text-[10px] ah-muted">
              You have {coinUnlock.balance} {coinUnlock.coin_name}
            </span>
          </div>
        )}
        {coinUnlockErr && (
          <p className="text-[10px] text-coral font-bold mt-1" data-testid={`block-coupon-coin-unlock-err-${block.block_id}`}>
            {coinUnlockErr}
          </p>
        )}
        {c.social_link && (
          <div className="mt-2 flex justify-start">
            <WatchReelButton url={c.social_link} testid={`block-coupon-reel-${block.block_id}`} />
          </div>
        )}
        {showAuth && !editable && (
          <PurchaseAuthGate
            creatorSlug={creatorSlug}
            intent="claim"
            priceInr={unlockPrice}
            itemTitle={c.teaser_headline || c.headline}
            onClose={() => setShowAuth(false)}
            onAuthed={() => { setShowAuth(false); setShowBuy(true); }}
          />
        )}
        {showBuy && !editable && (
          <PaidCouponCheckout
            couponId={couponId}
            unlockPrice={unlockPrice}
            storageKey={storageKey}
            buyerUser={user}
            onClose={() => setShowBuy(false)}
            onSuccess={(unlockedData) => {
              setUnlocked(unlockedData);
              setShowBuy(false);
              try { localStorage.setItem(storageKey, JSON.stringify(unlockedData)); } catch { /* noop */ }
            }}
          />
        )}
      </div>
    );
  }

  // Unlocked (or free) view — show the code + copy button.
  const displayHeadline = unlocked?.headline || c.headline || c.teaser_headline || `${c.discount_pct || unlocked?.discount_pct || ""}% off`;
  const displayDescription = unlocked?.description || c.description;
  const displayTerms = unlocked?.terms || c.terms;

  return (
    <div className="ah-block w-full" data-testid={`block-coupon-${block.block_id}`}>
      <button
        type="button"
        onClick={() => copy(code)}
        className="w-full text-left flex items-center gap-3"
      >
        <Tag className="w-5 h-5 flex-shrink-0 ah-accent" strokeWidth={2.5} />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate">{displayHeadline}</p>
          <code className="font-mono font-bold text-base tracking-widest ah-accent">{code || "CODE"}</code>
          {displayDescription && <p className="text-xs ah-muted mt-1 line-clamp-1">{displayDescription}</p>}
        </div>
        <span className="text-xs font-bold flex items-center gap-1 flex-shrink-0">
          {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </span>
      </button>
      {displayTerms && (
        <p className="text-[10px] ah-muted mt-2 italic line-clamp-2">Terms: {displayTerms}</p>
      )}
      {c.social_link && (
        <div className="mt-2 flex justify-start">
          <WatchReelButton url={c.social_link} testid={`block-coupon-reel-${block.block_id}`} />
        </div>
      )}
      {unlocked?.purchased_at && (
        <p className="text-[10px] ah-muted mt-1 flex items-center gap-1">
          <CheckCircle2 className="w-2.5 h-2.5" /> Purchased · saved to this device
        </p>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// PaidCouponCheckout — email → Razorpay → verify → store unlocked data.
// ---------------------------------------------------------------------------
export function PaidCouponCheckout({ couponId, unlockPrice, buyerUser, onClose, onSuccess }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [email, setEmail] = useState(buyerUser?.email || "");
  const [name, setName] = useState(buyerUser?.name || "");
  const [step, setStep] = useState("form"); // form | processing
  const [error, setError] = useState("");
  // v145.26 — When auth resolves after mount, backfill any still-blank
  // field so users don't have to re-type their email/name.
  useEffect(() => {
    if (!buyerUser) return;
    if (buyerUser.email) setEmail((v) => v || buyerUser.email);
    if (buyerUser.name) setName((v) => v || buyerUser.name);
  }, [buyerUser?.user_id]);

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Enter your email"); return; }
    setStep("processing");
    try {
      const orderRes = await fetch(`${backend}/api/public/coupons/${encodeURIComponent(couponId)}/purchase-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ buyer_email: email.trim(), buyer_name: name.trim() || undefined }),
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) throw new Error(orderData?.detail || "Could not create order");
      if (orderData.already_purchased) {
        onSuccess(orderData.coupon);
        return;
      }
      const { purchase_id, order } = orderData;
      // Mock flow (no Razorpay keys) — auto-verify.
      if (order.mock) {
        const vres = await fetch(`${backend}/api/public/coupons/${encodeURIComponent(couponId)}/purchase-verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            purchase_id,
            razorpay_order_id: order.id,
            razorpay_payment_id: `mock_pay_${Date.now()}`,
          }),
        });
        const vdata = await vres.json();
        if (!vres.ok) throw new Error(vdata?.detail || "Verify failed");
        onSuccess(vdata.coupon);
        return;
      }
      // Real Razorpay flow.
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment SDK");
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: cleanText("Linkola", 60),
        description: cleanText("Unlock exclusive coupon"),
        order_id: order.id,
        prefill: cleanPrefill({ email, name }),
        handler: async (resp) => {
          try {
            const vres = await fetch(`${backend}/api/public/coupons/${encodeURIComponent(couponId)}/purchase-verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                purchase_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }),
            });
            const vdata = await vres.json();
            if (!vres.ok) { setError(vdata?.detail || "Verify failed"); setStep("form"); return; }
            onSuccess(vdata.coupon);
          } catch (e2) {
            setError(e2.message || "Verify failed");
            setStep("form");
          }
        },
        modal: { ondismiss: () => setStep("form") },
      });
      rz.open();
    } catch (e2) {
      setError(e2.message || "Something went wrong");
      setStep("form");
    }
  };

  return (
    createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchEnd={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="paid-coupon-checkout"
    >
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="bg-white border-4 border-ink shadow-brutal w-full sm:max-w-sm h-[92dvh] sm:h-auto sm:max-h-[92dvh] overflow-y-auto p-5 space-y-3 rounded-t-lg sm:rounded-none"
      >
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5" strokeWidth={2.5} />
          <h3 className="font-black text-lg">Unlock this coupon</h3>
        </div>
        <p className="text-xs text-ink/70">
          Pay <b>₹{unlockPrice}</b> once to reveal the code. It stays saved on this device.
        </p>
        <input
          type="email" required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          autoComplete="email"
          data-testid="paid-coupon-email"
          className="w-full border-2 border-ink px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          data-testid="paid-coupon-name"
          maxLength={80}
          className="w-full border-2 border-ink px-3 py-2 text-sm"
        />
        {error && <p className="text-xs text-coral font-bold" data-testid="paid-coupon-error">{error}</p>}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={step === "processing"}
            data-testid="paid-coupon-pay-btn"
            className="flex-1 border-2 border-ink bg-coral text-white px-3 py-2 font-bold text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {step === "processing" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Pay ₹{unlockPrice} &amp; unlock
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            data-testid="paid-coupon-cancel-btn"
            className="border-2 border-ink bg-white px-3 py-2 font-bold text-sm shadow-brutalSm"
          >
            Cancel
          </button>
        </div>
        <p className="text-[10px] text-ink/50 text-center">
          Powered by Razorpay · Secure checkout
        </p>
      </form>
    </div>,
    document.body,
    )
  );
}

// ---------------------------------------------------------------------------
// FeaturedProductBlock — v114 hero-sized product spotlight.
// Renders a big image + headline + price + Buy/Book button. Delegates to the
// exact same StorefrontBuyModal as StorefrontCard so services (date+slot),
// coupons, OTP, address, and instructions/terms all work identically.
// ---------------------------------------------------------------------------
export function FeaturedProductBlock({ block, hubContext, editable }) {
  const c = block.config || {};
  const { user } = useAuth();
  const [openAuth, setOpenAuth] = useState(false);
  const [openBuy, setOpenBuy] = useState(false);
  // v147.75 — Bottom-sheet with full product description + image
  // gallery. Fires on image tap so tapping the poster doesn't
  // accidentally kick off checkout.
  const [openInfo, setOpenInfo] = useState(false);
  const products = hubContext?.products || [];
  const product = c.product_id
    ? products.find((p) => p.product_id === c.product_id)
    : null;
  // v146.42 — Loyalty hook must be called before any early returns to
  // stay compliant with React's rules-of-hooks.
  const slug = hubContext?.creator?.slug || hubContext?.slug || null;
  const loyalty = useCreatorLoyalty(slug);
  // Editor placeholder when no product is linked yet.
  if (editable && !product) {
    return (
      <div className="ah-block border-2 border-dashed border-ink/40 p-6 text-center text-xs ah-muted"
           data-testid={`block-featured-product-preview-${block.block_id}`}>
        <p className="font-bold text-sm ah-body">Featured product</p>
        <p>Pick a product or service above to spotlight it as a big hero card.</p>
      </div>
    );
  }
  if (!product) return null;

  const isService = String(product.kind || "product").toLowerCase() === "service";
  const isTicket = String(product.kind || "product").toLowerCase() === "ticket";
  const isINR = String(product.currency || "INR").toUpperCase() === "INR";
  const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£" }[String(product.currency || "INR").toUpperCase()] || "₹";
  const hasPrice = Number(product.price) > 0;
  const canBuyNow = isINR && hasPrice && !product.buy_url;
  const buyLabel = isService ? "Book now" : (isTicket ? "Get tickets" : "Buy now");
  // v145.61 — Creator pass-on discount on affiliate picks. When set,
  // show the shop price crossed-out next to the Linkola price + savings.
  const listPrice = Number(product.list_price_inr || 0);
  const discountPct = Number(product.customer_discount_pct || 0);
  const savings = Number(product.customer_savings_inr || 0);
  const hasCreatorDiscount = product.is_affiliate && discountPct > 0 && listPrice > Number(product.price || 0);
  // v145.62 — Social proof for affiliate hero.
  const socialProofCount = Number(product.social_proof_count || 0);
  const showSocialProof = !!product.is_affiliate && socialProofCount > 0;
  const coinsPreview = loyalty.coinsForAmount(Number(product.price || 0));
  const requireOtp = !!(hubContext?.settings?.require_phone_otp);
  const otpChannel = hubContext?.settings?.otp_channel || "auto";
  const align = c.align === "left" ? "left" : "center";
  const headline = (c.headline || "").trim() || product.title;
  const subheadline = (c.subheadline || "").trim();
  // v147.76 — Surface the same at-a-glance chips the storefront grid
  // card shows (event date/time, venue, seats left, "from ₹X" for
  // tiered tickets) so the Featured block isn't a purely visual hero
  // with no context. Users can now scan the bio hub and decide before
  // tapping Buy.
  const fmtShortDate = (iso) => {
    if (!iso) return "";
    try {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return iso; }
  };
  const hasEventDate = isTicket && !!product.event_date_from;
  const eventDateLabel = hasEventDate
    ? (product.event_date_to && product.event_date_to !== product.event_date_from
        ? `${fmtShortDate(product.event_date_from)} – ${fmtShortDate(product.event_date_to)}`
        : fmtShortDate(product.event_date_from))
      + (product.event_time ? ` · ${product.event_time}` : "")
    : "";
  const totalTickets = Number(product.total_tickets || 0);
  const ticketsSold = Number(product.tickets_sold || 0);
  const ticketsLeft = isTicket && totalTickets > 0 ? Math.max(0, totalTickets - ticketsSold) : null;
  const isTicketSoldOut = isTicket && totalTickets > 0 && ticketsLeft === 0;
  const tiers = Array.isArray(product.ticket_tiers) ? product.ticket_tiers : [];
  const tieredFromPrice = isTicket && tiers.length > 1
    ? Math.min(...tiers.map((t) => Number(t.price || 0)).filter((n) => n > 0))
    : null;


  // Big centered image + copy stacked (default) OR two-column with image
  // left, copy right (left align).
  const container =
    "ah-block border-4 border-ink bg-white shadow-brutal overflow-hidden";
  // v147.73 — Multi-image gallery + no-crop display. When the product
  // has `image_urls[]` (or a legacy single `image_url`) we render a
  // horizontally scrollable snap-carousel. Every image uses
  // `object-contain` on a cream backdrop so tall event posters and
  // wide product shots both fit without being cropped. Height is bounded
  // (max ~85% viewport) so ridiculously tall assets can't push the CTA
  // below the fold.
  const gallery = (
    <button
      type="button"
      onClick={() => setOpenInfo(true)}
      className="block w-full text-left focus:outline-none focus:ring-4 focus:ring-ink/20"
      aria-label={`Preview ${product.title}`}
      data-testid={`featured-product-image-btn-${block.block_id}`}
    >
      <ProductImageCarousel
        images={product.image_urls && product.image_urls.length
          ? product.image_urls
          : (product.image_url ? [product.image_url] : [])}
        name={product.title}
        align={align}
        testIdPrefix={`featured-product-image-${block.block_id}`}
      />
    </button>
  );

  const copy = (
    <div className={`p-5 sm:p-6 flex flex-col gap-3 ${align === "center" ? "text-center items-center" : "text-left items-start"}`}>
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <span className="text-[10px] font-bold uppercase tracking-wider border border-ink px-1.5 py-0.5 bg-sun">Featured</span>
        {product.is_best_seller && (
          <span className="text-[10px] font-bold uppercase tracking-wider border border-ink px-1.5 py-0.5 bg-mint inline-flex items-center gap-1" data-testid={`featured-product-bestseller-badge-${block.block_id}`}>
            🏆 Best seller
          </span>
        )}
        {isService && (
          <span className="text-[10px] font-bold uppercase tracking-wider border border-ink px-1.5 py-0.5 bg-lavender" data-testid={`featured-product-service-badge-${block.block_id}`}>Service</span>
        )}
        {showSocialProof && (
          <span
            className="text-[10px] font-bold uppercase tracking-wider border-2 border-ink px-1.5 py-0.5 bg-mint inline-flex items-center gap-1"
            data-testid={`featured-product-social-proof-${block.block_id}`}
          >
            <Users className="w-2.5 h-2.5" strokeWidth={2.5} />
            {socialProofCount.toLocaleString("en-IN")} bought
          </span>
        )}
      </div>
      <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter leading-tight" data-testid={`featured-product-headline-${block.block_id}`}>
        {headline}
      </h2>
      {subheadline && (
        <p className="text-sm text-ink/70" data-testid={`featured-product-subheadline-${block.block_id}`}>{subheadline}</p>
      )}
      {/* v147.76 — Event details strip for tickets: date+time, venue,
          seats-left chip. Same layout as the storefront grid card so
          bio-hub visitors get the essentials at a glance. */}
      {isTicket && (hasEventDate || product.venue_name || ticketsLeft != null) && (
        <div
          className={`flex flex-wrap items-center gap-1.5 text-[11px] w-full ${align === "center" ? "justify-center" : "justify-start"}`}
          data-testid={`featured-product-event-strip-${block.block_id}`}
        >
          {hasEventDate && (
            <span className="font-semibold inline-flex items-center gap-1" data-testid={`featured-product-event-date-${block.block_id}`}>
              📅 {eventDateLabel}
            </span>
          )}
          {product.venue_name && (
            <span className="text-ink/70 inline-flex items-center gap-1" data-testid={`featured-product-event-venue-${block.block_id}`}>
              📍 {product.venue_name}
            </span>
          )}
          {ticketsLeft != null && (
            <span
              className={`font-black uppercase px-1.5 py-0.5 border-2 border-ink ${
                isTicketSoldOut ? "bg-coral text-white"
                : ticketsLeft <= 10 ? "bg-sun"
                : "bg-mint"
              }`}
              data-testid={`featured-product-tickets-left-${block.block_id}`}
            >
              {isTicketSoldOut ? "Sold out" : `${ticketsLeft} left`}
            </span>
          )}
          {/* v147.93 — Public live attendance widget. Self-hides
              unless the creator toggled `live_widget_enabled` for
              this event. Refreshes every 30s. */}
          <EventAttendanceChip
            productId={product.product_id}
            testId={`featured-product-attendance-chip-${block.block_id}`}
          />
        </div>
      )}
      {/* v147.76 — Service quick-facts: slot count + weekday
          availability so bookable services show their scheduling
          affordances too. */}
      {isService && (Number(product.slots) > 0 || (product.available_days || []).length > 0) && (
        <div
          className={`flex flex-wrap items-center gap-1.5 text-[11px] w-full ${align === "center" ? "justify-center" : "justify-start"}`}
          data-testid={`featured-product-service-strip-${block.block_id}`}
        >
          {Number(product.slots) > 0 && (
            <span className="font-semibold inline-flex items-center gap-1">
              🗓 {product.slots} slot{product.slots > 1 ? "s" : ""} per day
            </span>
          )}
          {(product.available_days || []).length > 0 && (
            <span className="text-ink/70">
              · {product.available_days.map((d) => d.slice(0, 3)).join(", ")}
            </span>
          )}
        </div>
      )}
      {product.description && !subheadline && (
        <button
          type="button"
          onClick={() => setOpenInfo(true)}
          className="block text-left w-full"
          data-testid={`featured-product-desc-btn-${block.block_id}`}
        >
          <p className="text-sm text-ink/70 line-clamp-3">
            {truncateWords(product.description, 200)}
          </p>
          {product.description.length > 200 && (
            <span className="text-[11px] font-bold text-coral underline underline-offset-2 mt-1 inline-block">
              Read more
            </span>
          )}
        </button>
      )}
      {hasPrice && (
        hasCreatorDiscount ? (
          <div className="space-y-1" data-testid={`featured-product-price-${block.block_id}`}>
            <div className="flex items-baseline gap-3 flex-wrap">
              <p className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-coral"
                 data-testid={`featured-product-linkola-price-${block.block_id}`}>
                {sym}{Number(product.price).toFixed(0)}
              </p>
              <p className="text-sm text-ink/50 line-through tabular-nums"
                 data-testid={`featured-product-shop-price-${block.block_id}`}>
                Shop {sym}{listPrice.toLocaleString("en-IN")}
              </p>
            </div>
            <span className="inline-block text-[10px] font-black uppercase tracking-wider border-2 border-ink bg-sun px-1.5 py-0.5"
                  data-testid={`featured-product-savings-${block.block_id}`}>
              Save {sym}{Math.round(savings)} · {discountPct}% off on Linkola
            </span>
          </div>
        ) : (
          <p className="font-display font-black text-3xl sm:text-4xl tracking-tighter" data-testid={`featured-product-price-${block.block_id}`}>
            {tieredFromPrice != null
              ? <>from {sym}{Number(tieredFromPrice).toFixed(0)}</>
              : <>{sym}{Number(product.price).toFixed(0)}</>}
            <span className="text-xs text-ink/50 font-bold ml-2">
              {product.currency || "INR"}
            </span>
          </p>
        )
      )}
      {product.buy_url ? (
        <a
          href={product.buy_url}
          target="_blank"
          rel="noreferrer"
          onClick={externalLinkClickHandler(product.buy_url, (e) => hubContext?.onProductClick && hubContext.onProductClick(e, product))}
          data-testid={`featured-product-buy-external-${block.block_id}`}
          className="border-2 border-ink bg-coral text-white px-5 py-2.5 font-bold text-sm shadow-brutalSm inline-flex items-center gap-2"
        >
          <ShoppingBag className="w-4 h-4" /> {isService ? "Book" : "Buy"}{hasPrice ? ` — ${sym}${Number(product.price).toFixed(0)}` : ""}
        </a>
      ) : canBuyNow ? (
        <button
          type="button"
          disabled={isTicketSoldOut}
          onClick={() => (user?.user_id ? setOpenBuy(true) : setOpenAuth(true))}
          data-testid={`featured-product-buy-btn-${block.block_id}`}
          className={`border-2 border-ink px-5 py-2.5 font-bold text-sm shadow-brutal inline-flex items-center gap-2 transition-transform ${
            isTicketSoldOut ? "bg-white text-ink/50 cursor-not-allowed"
              : "bg-coral text-white hover:translate-y-[-1px]"
          }`}
        >
          <ShoppingBag className="w-4 h-4" /> {
            isTicketSoldOut ? "Sold out"
              : `${buyLabel} — ${sym}${Number(tieredFromPrice != null ? tieredFromPrice : product.price).toFixed(0)}${tieredFromPrice != null ? "+" : ""}`
          }
        </button>
      ) : (
        // v145.60 — No price + no buy_url ⇒ hide the buy CTA and prompt
        // visitors to reach out instead of showing a ₹0 button.
        <span className="text-xs text-ink/50 italic" data-testid={`featured-product-nocta-${block.block_id}`}>Contact seller</span>
      )}
      {coinsPreview > 0 && (
        <CoinEarnChip
          coins={coinsPreview}
          currentBalance={loyalty.balance}
          coinName={loyalty.coin_name}
          size="md"
          className="mt-1 max-w-xs"
          testId={`featured-product-earn-${block.block_id}`}
        />
      )}
    </div>
  );

  return (
    <div className={container} data-testid={`block-featured-product-${block.block_id}`}>
      {align === "center" ? (
        <>
          {gallery}
          {copy}
        </>
      ) : (
        <div className="grid sm:grid-cols-[45%_1fr]">
          {gallery}
          {copy}
        </div>
      )}
      {openAuth && (
        <PurchaseAuthGate
          creatorSlug={slug}
          intent={isService ? "book" : "buy"}
          priceInr={Number(product.price) || 0}
          itemTitle={product.title || headline}
          onClose={() => setOpenAuth(false)}
          onAuthed={() => { setOpenAuth(false); setOpenBuy(true); }}
        />
      )}
      {openBuy && (
        <StorefrontBuyModal
          product={{ ...product, creator_user_id: product.creator_user_id || hubContext?.creator?.user_id }}
          slug={slug}
          requireOtp={requireOtp}
          otpChannel={otpChannel}
          codEnabled={!!hubContext?.settings?.cod_enabled}
          onlineEnabled={hubContext?.settings?.online_enabled !== false}
          buyerUser={user}
          onClose={() => setOpenBuy(false)}
          onSuccess={() => setOpenBuy(false)}
        />
      )}
      {/* v147.75 — Full product info sheet (image gallery + full 2000-char
          description). Opens when the buyer taps the poster or the
          truncated description "Read more" chip.
          v147.99.16 — Wire an in-sheet Buy Now / Book Now CTA so buyers
          can commit to purchase without closing the sheet first. Falls
          through to the same auth-gate → StorefrontBuyModal path used
          by the outer CTA. External `buy_url` products skip the sheet
          CTA (the outer button already routes off-site). */}
      <ProductInfoSheet
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        product={{ ...product, kind: isService ? "service" : (product.kind || "product") }}
        onCTA={!product.buy_url && canBuyNow && !isTicketSoldOut
          ? () => {
              setOpenInfo(false);
              if (user?.user_id) setOpenBuy(true);
              else setOpenAuth(true);
            }
          : undefined}
        ctaLabel={isService ? `Book Now — ${sym}${Number(product.price || 0).toFixed(0)}` : `Buy Now — ${sym}${Number(product.price || 0).toFixed(0)}`}
        ctaTestId={`product-info-sheet-cta-${block.block_id}`}
        ctaDisabled={!!isTicketSoldOut}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// BuyNowGridButton — v97 tiny Buy Now trigger used inside `products_grid`
// tiles when a product has an INR price but no external `buy_url`.
// ---------------------------------------------------------------------------
export function BuyNowGridButton({ product, testid, creatorSlug, buyerUser, codEnabled = false, onlineEnabled = true }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testid}
        className="border-2 border-ink bg-coral text-white px-2 py-1 font-bold text-[10px] shadow-brutalSm"
      >
        Buy now
      </button>
      {open && (
        // v145.20 — Route through StorefrontBuyModal so grid Buy Now
        // supports Loyalty Coin redemption + coupons out of the box.
        <StorefrontBuyModal
          product={product}
          slug={creatorSlug}
          buyerUser={buyerUser}
          codEnabled={codEnabled}
          onlineEnabled={onlineEnabled}
          onClose={() => setOpen(false)}
          onSuccess={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ProductBuyModal — v97 Razorpay Buy Now for Action Hub products.
// Opens a Razorpay checkout, verifies the signature server-side, and credits
// the creator's wallet with (amount * units − 10% platform fee). Legacy
// buy_url flow still available via the "Buy" link when Razorpay is off.
// ---------------------------------------------------------------------------
export function ProductBuyModal({ productId, title, unitPrice, currency = "INR", buyerUser, codEnabled = false, onlineEnabled = true, onClose }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [email, setEmail] = useState(buyerUser?.email || "");
  const [name, setName] = useState(buyerUser?.name || "");
  const [phone, setPhone] = useState(buyerUser?.phone || "");
  const [address, setAddress] = useState("");
  const [units, setUnits] = useState(1);
  const [step, setStep] = useState("form"); // form | processing | success | cod_success
  const [codOrderId, setCodOrderId] = useState(null);
  // v149.36 — The modal body is the scroll container. A buyer who
  // scrolled down to reach the Place-order button was left looking at
  // the middle of the confirmation (or at blank space) once the form
  // was swapped out, because the scroll offset survived the swap.
  const scrollRef = useRef(null);
  useEffect(() => {
    if (step !== "cod_success" && step !== "success") return;
    // `auto`, not `smooth`: on mobile Safari a smooth scroll started
    // while the content is being replaced is frequently dropped, and
    // the buyer is left mid-page with no way to know why.
    const el = scrollRef.current;
    try { el?.scrollTo({ top: 0, behavior: "auto" }); } catch { if (el) el.scrollTop = 0; }
    if (el) el.scrollTop = 0;
  }, [step]);
  const [error, setError] = useState("");
  // v146.44 — WhatsApp broadcast opt-in, default ON. Piped into
  // /public/products/{id}/order so the platform can persist consent in
  // db.marketing_consents keyed by (brand_user_id, phone).
  const [marketingConsent, setMarketingConsent] = useState(true);
  // v146 — Payment method selector. Default to Razorpay so behaviour
  // is unchanged for creators who haven't enabled COD (the toggle
  // block below just doesn't render).
  // v149.70 — "Cash on Delivery only" shop: COD is the method, no choice.
  const codOnly = codEnabled && !onlineEnabled;
  const [paymentMethod, setPaymentMethod] = useState(codOnly ? "cod" : "razorpay"); // razorpay | cod
  React.useEffect(() => { if (codOnly) setPaymentMethod("cod"); }, [codOnly]);
  // v145.26 — Backfill blank email/name when auth resolves after mount.
  useEffect(() => {
    if (!buyerUser) return;
    if (buyerUser.email) setEmail((v) => v || buyerUser.email);
    if (buyerUser.name) setName((v) => v || buyerUser.name);
  }, [buyerUser?.user_id]);
  const total = Math.max(0, Number(unitPrice || 0) * Math.max(1, Number(units) || 1));

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!productId) { setError("Missing product"); return; }
    if (!email.trim()) { setError("Enter your email"); return; }
    // v146 — COD requires a delivery address so the brand knows where
    // to send / call the buyer. Phone is strongly recommended too.
    if (paymentMethod === "cod") {
      if (!phone.trim()) { setError("Add a phone number so the seller can reach you."); return; }
      if (!address.trim()) { setError("Add a delivery address for COD."); return; }
    }
    setStep("processing");
    try {
      const orderRes = await fetch(`${backend}/api/public/products/${encodeURIComponent(productId)}/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          buyer_email: email.trim(),
          buyer_name: name.trim() || undefined,
          buyer_phone: phone.trim() || undefined,
          buyer_address: address.trim() || undefined,
          units: Math.max(1, Math.min(100, Number(units) || 1)),
          payment_method: paymentMethod,
          marketing_consent: marketingConsent,
        }),
      });
      const orderRaw = await orderRes.text();
      let orderData = {};
      try { orderData = orderRaw ? JSON.parse(orderRaw) : {}; } catch { /* keep {} */ }
      if (!orderRes.ok) {
        throw new Error(orderData?.detail || `Could not create order (${orderRes.status}). Please try again.`);
      }
      // v146 — COD short-circuit. Backend returned `{ cod: true }` and
      // has already persisted the pending order. Skip Razorpay entirely.
      if (orderData.cod) {
        setCodOrderId(orderData.order_id || null);
        setStep("cod_success");
        return;
      }
      const { order_id, order } = orderData;
      const verify = async (payment_id, signature) => {
        const vres = await fetch(`${backend}/api/public/products/${encodeURIComponent(productId)}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            order_id,
            razorpay_order_id: order.id,
            razorpay_payment_id: payment_id,
            razorpay_signature: signature,
          }),
        });
        const vRaw = await vres.text();
        let vdata = {};
        try { vdata = vRaw ? JSON.parse(vRaw) : {}; } catch { /* keep {} */ }
        if (!vres.ok) {
          throw new Error(vdata?.detail || `Verify failed (${vres.status}). Your payment may still succeed — please refresh in a minute.`);
        }
        setStep("success");
      };
      // Mock flow (no Razorpay keys) — auto-verify.
      if (order.mock) {
        await verify(`mock_pay_${Date.now()}`, undefined);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment SDK");
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: cleanText("Linkola", 60),
        description: cleanText(title || "Product purchase"),
        order_id: order.id,
        prefill: cleanPrefill({ email, name, contact: phone }),
        handler: async (resp) => {
          try {
            await verify(resp.razorpay_payment_id, resp.razorpay_signature);
          } catch (e2) {
            setError(e2.message || "Verify failed");
            setStep("form");
          }
        },
        modal: { ondismiss: () => setStep("form") },
      });
      rz.open();
    } catch (e2) {
      setError(e2.message || "Something went wrong");
      setStep("form");
    }
  };

  return (
    createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchEnd={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="product-buy-modal"
    >
      <form
        ref={scrollRef}
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="bg-white border-4 border-ink shadow-brutal w-full sm:max-w-sm h-[92dvh] sm:h-auto sm:max-h-[92dvh] overflow-y-auto p-5 space-y-3 rounded-t-lg sm:rounded-none"
      >
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5" strokeWidth={2.5} />
          <h3 className="font-black text-lg">Buy {title || "product"}</h3>
        </div>
        {step === "success" ? (
          <>
            <p className="text-sm">
              Payment received. The seller has been notified and will get in touch.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="product-buy-close-btn"
              className="w-full border-2 border-ink bg-mint px-3 py-2 font-bold text-sm shadow-brutalSm"
            >
              Done
            </button>
          </>
        ) : step === "cod_success" ? (
          <>
            <div className="border-2 border-ink bg-mint p-3 text-sm space-y-1" data-testid="product-buy-cod-success">
              <p className="font-black">Order placed — pending confirmation</p>
              {codOrderId && (
                <p className="text-[11px]">
                  <span className="font-black uppercase tracking-widest text-[9px]">Order number</span><br />
                  <span className="font-mono font-bold break-all" data-testid="product-buy-cod-order-id">{codOrderId}</span>
                </p>
              )}
              <p className="text-xs">
                The seller has been notified and will confirm your COD order shortly. You&apos;ll receive a confirmation email once accepted.
              </p>
            </div>
            <p className="text-[11px] text-ink/60 leading-snug">
              Keep <b>₹{total.toFixed(2)}</b> ready at delivery / pickup. Payment is collected only after the seller confirms.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="product-buy-cod-close-btn"
              className="w-full border-2 border-ink bg-white px-3 py-2 font-bold text-sm shadow-brutalSm"
            >
              Done
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-ink/70">
              ₹{Number(unitPrice).toFixed(2)} × <span className="font-bold">{units}</span> = <b>₹{total.toFixed(2)}</b>
            </p>

            {/* v146 — Payment method selector. Only rendered when the
                creator has enabled COD in their Commerce & Payments
                settings. Default remains "Pay Now" so behaviour is
                identical when COD is off. */}
            {codOnly && (
              <div className="border-2 border-ink bg-cream p-2" data-testid="product-buy-payment-method">
                <p className="text-[11px] font-bold" data-testid="product-buy-cod-only">
                  Cash on Delivery
                  <span className="block font-normal text-ink/70 mt-0.5">
                    This seller takes payment in cash at delivery / pickup.
                  </span>
                </p>
              </div>
            )}
            {codEnabled && !codOnly && (
              <div className="border-2 border-ink bg-cream p-2" data-testid="product-buy-payment-method">
                <p className="text-[10px] font-black uppercase tracking-wider mb-1.5">Payment method</p>
                <div className="flex gap-1.5">
                  {[
                    { key: "razorpay", label: "Pay Now" },
                    { key: "cod", label: "Cash on Delivery" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => { setPaymentMethod(opt.key); setError(""); }}
                      data-testid={`product-buy-pm-${opt.key}`}
                      aria-pressed={paymentMethod === opt.key}
                      className={`flex-1 border-2 border-ink px-2 py-1.5 text-[11px] font-bold ${
                        paymentMethod === opt.key
                          ? "bg-ink text-white"
                          : "bg-white hover:bg-cream"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <label className="text-xs font-bold flex-shrink-0">Units</label>
              <input
                type="number"
                min={1}
                max={100}
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                data-testid="product-buy-units"
                className="w-24 border-2 border-ink px-3 py-2 text-sm"
              />
            </div>
            <input
              type="email" required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              autoComplete="email"
              data-testid="product-buy-email"
              className="w-full border-2 border-ink px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              maxLength={80}
              data-testid="product-buy-name"
              className="w-full border-2 border-ink px-3 py-2 text-sm"
            />
            {/* COD-only fields — required for delivery. */}
            {paymentMethod === "cod" && (
              <>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone (for delivery)"
                  autoComplete="tel"
                  maxLength={20}
                  data-testid="product-buy-cod-phone"
                  className="w-full border-2 border-ink px-3 py-2 text-sm font-mono"
                />
                <MarketingConsentCheckbox
                  checked={marketingConsent}
                  onChange={setMarketingConsent}
                  testid="product-buy-consent"
                  compact
                />
                <textarea
                  required
                  rows={2}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Delivery address"
                  maxLength={500}
                  data-testid="product-buy-cod-address"
                  className="w-full border-2 border-ink px-3 py-2 text-sm"
                />
              </>
            )}
            {error && <p className="text-xs text-coral font-bold" data-testid="product-buy-error">{error}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={step === "processing"}
                data-testid="product-buy-pay-btn"
                className="flex-1 border-2 border-ink bg-coral text-white px-3 py-2 font-bold text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {step === "processing" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {paymentMethod === "cod"
                  ? `Place COD Order — ₹${total.toFixed(2)}`
                  : `Pay ₹${total.toFixed(2)}`}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                data-testid="product-buy-cancel-btn"
                className="border-2 border-ink bg-white px-3 py-2 font-bold text-sm shadow-brutalSm"
              >
                Cancel
              </button>
            </div>
            <p className="text-[10px] text-ink/50 text-center">
              Powered by Razorpay · Currency: {currency}
            </p>
          </>
        )}
      </form>
    </div>,
    document.body,
    )
  );
}

export function PaidCouponBlock({ block, editable, onClick, hubContext }) {
  const c = block.config || {};
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const campaignId = c.campaign_id;
  const price = Number(c.unlock_price || 0);
  const remaining = c.remaining;
  const status = c.status;
  const expired = !!c.expired;
  const creatorSlug = hubContext?.creator?.slug || hubContext?.slug || null;
  if (!campaignId) {
    return editable ? (
      <div className="ah-block w-full text-center bg-cream border-2 border-dashed border-ink/40 py-4 text-xs text-ink/60" data-testid={`block-paidcoupon-empty-${block.block_id}`}>
        Pick a paid coupon campaign in this block&apos;s settings.
      </div>
    ) : null;
  }
  // v146.26 — When the bio page owner isn't the campaign creator (i.e. they
  // picked this voucher from the marketplace), append `?s={owner_user_id}`
  // so the checkout can flow the influencer share to them.
  const bioOwnerId = hubContext?.creator?.user_id || hubContext?.user_id || null;
  const campCreatorId = c.creator_user_id || c.influencer_user_id || null;
  const isReseller = !!(c.voucher_mode && c.sold_by_others && bioOwnerId
                        && campCreatorId && bioOwnerId !== campCreatorId);
  const url = isReseller
    ? `/coupon/${campaignId}?s=${encodeURIComponent(bioOwnerId)}`
    : `/coupon/${campaignId}`;

  // v104 — Human-friendly validity hint.
  const endDateStr = c.end_date ? new Date(c.end_date).toLocaleDateString() : null;
  const validityLine = endDateStr
    ? `Valid until ${endDateStr}`
    : c.valid_days_after_purchase
      ? `Redeem within ${c.valid_days_after_purchase} days of purchase`
      : null;

  const inner = (
    <>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 flex-shrink-0 border-2 border-ink bg-coral text-white flex items-center justify-center" style={{ boxShadow: "2px 2px 0 var(--ah-fg)" }}>
          {expired ? <Timer className="w-4 h-4" strokeWidth={2.5} /> : <Lock className="w-4 h-4" strokeWidth={2.5} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate">{c.title || "Exclusive discount"}</p>
          <p className="text-[11px] ah-muted truncate">
            {c.business_name ? `${c.business_name}${c.business_category ? ` · ${c.business_category}` : ""}` : "Tap to unlock"}
          </p>
        </div>
        <span className={`text-xs font-bold flex-shrink-0 px-2 py-1 border-2 border-ink ${expired ? "bg-ink/10 text-ink/60" : "bg-coral text-white"}`}>
          {expired ? "EXPIRED" : price > 0 ? `₹${price}` : "FREE"}
        </span>
      </div>

      {/* v146.25 — Voucher preview chip: ₹1000 for ₹900 (10% off face value) */}
      {c.voucher_mode && c.face_value && !expired && (
        <div className="mt-2 inline-flex items-center gap-1 border-2 border-ink bg-mint px-2 py-0.5 text-[11px] font-black" data-testid={`block-paidcoupon-voucher-${block.block_id}`}>
          <Gift className="w-3 h-3" strokeWidth={2.5} />
          ₹{Number(c.face_value).toFixed(0)} voucher for ₹{price}
          {c.face_value > price && (
            <span className="opacity-70">
              · save ₹{Number(c.face_value - price).toFixed(0)}
            </span>
          )}
        </div>
      )}

      {/* v104 — Offer summary chip (e.g. "20% off · on ₹500+ bills") */}
      {c.offer_summary && !expired && !c.voucher_mode && (
        <div className="mt-2 inline-flex items-center gap-1 border-2 border-ink bg-sun px-2 py-0.5 text-[11px] font-black">
          <Tag className="w-3 h-3" strokeWidth={2.5} /> {c.offer_summary}
        </div>
      )}

      {/* v104 — Teaser description */}
      {c.teaser_description && !expired && (
        <p className="text-[12px] ah-muted mt-1.5 line-clamp-2">{c.teaser_description}</p>
      )}

      {/* Validity / expiry hint */}
      {validityLine && (
        <p className="text-[10px] ah-muted mt-1.5 inline-flex items-center gap-1">
          <Timer className="w-3 h-3" /> {validityLine}
        </p>
      )}

      {expired && (
        <p className="text-[10px] font-bold text-ink/50 mt-1">This coupon is no longer accepting new unlocks.</p>
      )}

      {!expired && status === "active" && remaining !== undefined && remaining !== null && remaining < 50 && (
        <p className="text-[10px] font-bold text-coral mt-1">Only {remaining} left!</p>
      )}
    </>
  );
  if (editable) {
    return <div className="ah-block w-full block" data-testid={`block-paidcoupon-${block.block_id}`}>{inner}</div>;
  }
  // v144.4 — Anonymous visitors get an auth gate + coins-ribbon modal
  // before we route them to the coupon detail page. Signed-in users go
  // straight to `/coupon/{id}` (existing behaviour).
  const handleTap = (e) => {
    if (user?.user_id) {
      if (onClick) onClick(e, url);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setShowAuth(true);
  };
  return (
    <>
      <a href={url} target="_blank" rel="noopener noreferrer"
         onClick={externalLinkClickHandler(url, handleTap)}
         className="ah-block w-full block hover:translate-y-[-1px] transition-transform"
         data-testid={`block-paidcoupon-${block.block_id}`}>
        {inner}
      </a>
      {showAuth && (
        <PurchaseAuthGate
          creatorSlug={creatorSlug}
          intent="claim"
          priceInr={price}
          itemTitle={c.title || "Exclusive discount"}
          onClose={() => setShowAuth(false)}
          onAuthed={() => {
            setShowAuth(false);
            // After sign-in, open the coupon detail page in a new tab
            // just like the direct link would have.
            openExternalUrl(url);
          }}
        />
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// v89 (Feb 2026) — GoogleReviewBlock
// ---------------------------------------------------------------------------
// Modes:
//   * direct         → renders a single button that opens `google_review_url`
//   * internal_first → collect 1-5 star rating, ALWAYS redirect to Google
//   * gated          → collect 1-5 star rating; only redirect if rating >=
//                      `min_rating_for_google`. Lower ratings see the
//                      creator's `thanks_message`. When `collect_comment`
//                      is true, a short private comment is also captured.
// Ratings persist to `db.google_review_ratings` via the backend endpoint.
// ---------------------------------------------------------------------------
